import { UISref } from "@uirouter/react";
import { map } from "lodash";
import { FC, useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { PrimaryButton } from "../../../../../components/button";
import { RadioGroup } from "../../../../../components/forms/radio";
import { Flex, hiddenMobile } from "../../../../../components/layout";
import { WalletsRoutes } from "../../../../../pages/wallets/routes";
import { Pageable } from "../../../../../services/pageable";
import { useFetchable } from "../../../../common/fetchable";
import { isFetchableSuccess } from "../../../../common/fetchable/helpers";
import { WalletsDTO } from "../../../../wallets/models/dto";
import { Wallets } from "../../../../wallets/reducer";
import { ErrorText, RenderFuncProps } from "../common";
import { useAllowedChains } from "./common";

const DesktopCTA = styled(PrimaryButton)`
  flex-shrink: 0;
  margin-left: 1em;
  ${hiddenMobile}
`;

export const AuthenticatedSourceWallet: FC<RenderFuncProps> = ({ item }) => {
  const allowedChains = useAllowedChains(item);
  const { t } = useTranslation("fields");
  const allWallets = useFetchable(
    Wallets.all,
    useMemo<Pageable.T<keyof WalletsDTO.Responses.GetSingle.T>>(
      () => ({
        pagination: {
          size: 20,
          page: 0,
        },
        sorting: {
          direction: "asc",
          field: "chain",
        },
      }),
      []
    )
  );

  const options = useMemo(
    () => [
      ...(isFetchableSuccess(allWallets)
        ? allWallets.data.content.filter(({ chain }) =>
            allowedChains.includes(chain)
          )
        : []),
    ],
    [allWallets]
  );

  const { control } = useFormContext();
  const { field, fieldState, formState } = useController({
    control,
    name: item.field,
  });

  const error = useMemo(
    () =>
      fieldState.error ||
      formState.errors[`${item.field}.chain`] ||
      formState.errors[`${item.field}.address`],
    [formState, item.field]
  );

  return (
    <>
      <div>{t("source-wallet")}</div>
      <div>
        <Flex center>
          <RadioGroup<typeof options[number]["id"]>
            options={map(options, ({ id, address, chain }) => ({
              id,
              label: (
                <>
                  {chain}{" "}
                  <strong>
                    {" "}
                    {address.substring(0, 7)}...
                    {address.substring(address.length - 6, address.length)}
                  </strong>
                </>
              ),
            }))}
            onChange={(e) => field.onChange(options.find(({ id }) => id === e))}
            value={field.value?.id}
            fullWidth
            inline
          />

          <UISref to={WalletsRoutes.names.walletList}>
            <DesktopCTA variant="outlined" icon="wallet">
              {t("add-wallet")}
            </DesktopCTA>
          </UISref>
        </Flex>
        {!!error ? <ErrorText>{error?.message}</ErrorText> : null}
      </div>
    </>
  );
};
